import { FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Alert, Button, Field, Label, Paragraph, Select } from '@digdir/designsystemet-react';
import { useUpdateOrganizationMutation } from '../../hooks/mutations/useUpdateOrganizationMutation';
import { SettingsSection } from '../TeamSettingsLayout';
import classes from './WorkLanguageSettings.module.css';

export type WorkLanguageSettingsProps = {
  initialWorkLanguage: 'NB' | 'EN' | null | undefined;
};

type WorkLanguage = 'NB' | 'EN' | 'inherit';

/** Standardspråket for varsler, undersøkelser og meldinger i organisasjonen */
export const WorkLanguageSettings = ({ initialWorkLanguage }: WorkLanguageSettingsProps) => {
  const { t } = useTranslation();
  const { mutate: updateOrganization, isPending } = useUpdateOrganizationMutation();
  const [workLanguage, setWorkLanguage] = useState<WorkLanguage>(initialWorkLanguage ?? 'inherit');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Update local state when initialWorkLanguage changes
  useEffect(() => {
    if (initialWorkLanguage && workLanguage === 'inherit') {
      setWorkLanguage(initialWorkLanguage);
    }
  }, [initialWorkLanguage, workLanguage]);

  const handleSave = (event: FormEvent) => {
    event.preventDefault();
    setErrorMessage(null);

    updateOrganization(
      { workLanguage: workLanguage === 'inherit' ? null : workLanguage },
      {
        onSuccess: () => {
          toast.success(t('organizationSettings.toast.saveSuccess'), { position: 'bottom-right' });
        },
        onError: (error: any) => {
          setErrorMessage(error?.response?.data?.message || t('organizationSettings.errors.saveError'));
        },
      },
    );
  };

  return (
    <SettingsSection
      title={t('organizationSettings.workLanguage.title')}
      description={t('organizationSettings.workLanguage.description')}
    >
      <form className={classes.form} onSubmit={handleSave}>
        <Field className={classes.field}>
          <Label htmlFor="workLanguage">{t('organizationSettings.workLanguage.fields.workLanguage.label')}</Label>
          <Select
            id="workLanguage"
            name="workLanguage"
            value={workLanguage}
            disabled={isPending}
            onChange={(event) => {
              setErrorMessage(null);
              setWorkLanguage(event.target.value as WorkLanguage);
            }}
          >
            <Select.Option value="inherit">
              {t('organizationSettings.workLanguage.fields.workLanguage.options.systemDefault')}
            </Select.Option>
            <Select.Option value="NB">
              {t('organizationSettings.workLanguage.fields.workLanguage.options.norwegian')}
            </Select.Option>
            <Select.Option value="EN">
              {t('organizationSettings.workLanguage.fields.workLanguage.options.english')}
            </Select.Option>
          </Select>
        </Field>

        {errorMessage && (
          <Alert data-color="danger">
            <Paragraph>{errorMessage}</Paragraph>
          </Alert>
        )}

        <div>
          <Button type="submit" disabled={isPending}>
            {isPending ? t('teamSettings.common.saving') : t('organizationSettings.actions.save')}
          </Button>
        </div>
      </form>
    </SettingsSection>
  );
};
